import { ReactNode, startTransition, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import type { DateTime } from "luxon"
import { enqueueSnackbar } from "notistack"
import { Backdrop, Collapse, Skeleton, Stack } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { MERCHANT_ADMIN_SETTINGS_MENU_PLANNING_ROUTE } from "../../../../../../constants/routes"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { useDateTime } from "../../../../../localization/useDateTime"
import { ComingSoonDialog } from "../../../../../../shared/components/dialogs/ComingSoonDialog"
import { StandardNoResultText } from "../../../../../../shared/components/noResult/StandardNoResultText"
import { StandardSegmentedControl } from "../../../../../../shared/components/segmentedControl/StandardSegmentedControl"
import { fetchAllergens } from "../../../../admin/products/productsApi"

import { useWeekMenuStore } from "../useWeekMenuStore"
import { MOCK_MENU_TEMPLATES, mockTemplateDetailsFor } from "../weekMenuMockData"
import { WeekMenuProvider } from "../WeekMenuContext"
import { defaultAiSettings } from "../weekMenuAiBank"
import { buildWeekRows, newThemeDish, weekDayDates, weekKeyFor } from "../weekMenuLib"
import {
    collectRecentDishes,
    countDayFillProgress,
    countReadyDays,
    countRowFillProgress,
    countThemeFillProgress,
    startedCellCountForDay,
} from "../weekMenuSelectors"
import { WeekDayPublishStatus, WeekMenuDragSource } from "../weekMenuTypes"
import { useAiGeneration } from "../hooks/useAiGeneration"
import { useAutosavedLabel } from "../hooks/useAutosavedLabel"
import { useMockActionLoading } from "../hooks/useMockActionLoading"
import { AiBanner } from "../components/ai/AiBanner"
import { AiCustomizeDialog } from "../components/ai/AiCustomizeDialog"
import { AiOverwriteDialog } from "../components/ai/AiOverwriteDialog"
import { ClearWeekConfirmDialog } from "../components/dialogs/ClearWeekConfirmDialog"
import { CopyWeekDialog, CopyWeekTarget } from "../components/dialogs/CopyWeekDialog"
import { DayConfirmAction, DayConfirmDialog } from "../components/dialogs/DayConfirmDialog"
import { ThemeNameDialog } from "../components/dialogs/ThemeNameDialog"
import { WeekMenuAutosavedLabel } from "../components/WeekMenuAutosavedLabel"
import { WeekMenuEmptyState } from "../components/WeekMenuEmptyState"
import { WeekMenuHeaderActions } from "../components/WeekMenuHeaderActions"
import { WeekMenuLegend } from "../components/WeekMenuLegend"
import { WeekMenuTable } from "../components/table/WeekMenuTable"
import { WeekMenuTableSkeleton } from "../components/table/WeekMenuTableSkeleton"
import { WeekMenuToolbar } from "../components/WeekMenuToolbar"

const useStyles = makeStyles()((theme) => ({
    // Keeps the control at its own width instead of stretching across the column Stack.
    templateTabs: {
        alignSelf: "flex-start",
    },
    // Mirrors the segmented control and the toolbar card while the page loads.
    templateTabsSkeleton: {
        alignSelf: "flex-start",
        width: "30rem",
        height: "3rem",
        borderRadius: ".75rem",
    },
    toolbarSkeleton: {
        height: "4.25rem",
        borderRadius: ".625rem",
    },
    // Invisible click shield while something runs; only the raised banner stays usable.
    workingBackdrop: {
        zIndex: theme.zIndex.drawer,
    },
    aiBannerAboveBackdrop: {
        position: "relative",
        zIndex: theme.zIndex.drawer + 1,
    },
}))

const COPY_WEEK_TARGET_COUNT = 4

// Clicking through weeks or templates only rebuilds the grid once the clicks stop.
const SWITCH_DEBOUNCE_MS = 250

type ThemeDialogState = Readonly<{
    open: boolean
    dayIndex: number
    isNew: boolean
}>

type DayConfirmState = Readonly<{
    action: DayConfirmAction
    dayIndex: number
}>

/** The week and template the heavy grid is actually built for, committed once the clicks stop. */
type CommittedSwitch = Readonly<{
    weekStart: DateTime
    templateIndex: number
}>

type Props = Readonly<{
    /** Renders the page header with the week menu actions on the title row and the autosaved subtitle. */
    renderHeader: (actions?: JSX.Element, subtitle?: JSX.Element) => ReactNode
}>

export function WeekMenuTab({ renderHeader }: Props) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const dateTimeFactory = useDateTime()
    const navigate = useNavigate()

    const [weekStart, setWeekStart] = useState(() => dateTimeFactory.now().startOf("week"))
    const [selectedTemplateIndex, setSelectedTemplateIndex] = useState(0)
    // The label and the template control follow every click instantly; the grid follows this debounced
    // twin, so clicking through five weeks builds the week once instead of five times.
    const [committed, setCommitted] = useState<CommittedSwitch>(() => ({ weekStart, templateIndex: 0 }))
    const [collapsedCategories, setCollapsedCategories] = useState<ReadonlySet<number>>(new Set())
    const [themeDialog, setThemeDialog] = useState<ThemeDialogState>({ open: false, dayIndex: 0, isNew: true })
    const [isCopyWeekOpen, setCopyWeekOpen] = useState(false)
    const [isClearWeekConfirmOpen, setClearWeekConfirmOpen] = useState(false)
    const [dayConfirm, setDayConfirm] = useState<DayConfirmState | null>(null)
    const [dragSource, setDragSource] = useState<WeekMenuDragSource | null>(null)
    const [isAiCustomizeOpen, setAiCustomizeOpen] = useState(false)
    const [isAiOverwriteOpen, setAiOverwriteOpen] = useState(false)
    const [isComingSoonOpen, setComingSoonOpen] = useState(false)

    const handleTemplateChange = (templateId: string) => {
        const index = MOCK_MENU_TEMPLATES.findIndex((template) => String(template.id) === templateId)
        if (index === -1) return
        setSelectedTemplateIndex(index)
        // Category indexes and drag state belong to the template being left behind.
        setCollapsedCategories(new Set())
        setDragSource(null)
    }

    const handleWeekChange = (weeks: number) => {
        setWeekStart((start) => start.plus({ weeks }))
        setDragSource(null)
    }

    const allergensQuery = useApiQuery(fetchAllergens, {
        queryName: "allergens",
        dependencies: {},
        select: (data) => data.allergens,
    })
    const allergenOptions = allergensQuery.data ?? []

    // TODO: swap for fetchMenuTemplates once the week menu API exists.
    const templates = MOCK_MENU_TEMPLATES
    const selectedTemplate = templates[selectedTemplateIndex]
    // The grid, its store and the day headers all belong to the committed template and week.
    const activeTemplate = templates[committed.templateIndex]
    const templateDetails = activeTemplate ? mockTemplateDetailsFor(activeTemplate.id) : undefined

    const storeKey = `${activeTemplate?.id ?? "none"}:${weekKeyFor(committed.weekStart)}`
    const pendingStoreKey = `${selectedTemplate?.id ?? "none"}:${weekKeyFor(weekStart)}`
    const isSwitching = pendingStoreKey !== storeKey

    // startTransition lets React keep the skeleton responsive while it builds the new grid.
    useEffect(() => {
        if (!isSwitching) return
        const timer = window.setTimeout(
            () => startTransition(() => setCommitted({ weekStart, templateIndex: selectedTemplateIndex })),
            SWITCH_DEBOUNCE_MS,
        )
        return () => window.clearTimeout(timer)
    }, [isSwitching, weekStart, selectedTemplateIndex])

    const categories = useMemo(
        () => (templateDetails ? buildWeekRows(templateDetails, allergenOptions) : []),
        [templateDetails, allergenOptions],
    )
    const allRows = useMemo(() => categories.flatMap((category) => category.dishes), [categories])

    const { week, days, lastEditedAt, aiFilledKeys, actions } = useWeekMenuStore(storeKey, categories)

    const ai = useAiGeneration(days, allergenOptions, actions)
    const mockLoading = useMockActionLoading()
    const autosavedLabel = useAutosavedLabel(lastEditedAt)
    const isPageBusy = ai.isWorking || mockLoading.isBusy

    // Stable per week, so the memoized context value survives keystrokes.
    const dayStrings = useMemo(() => {
        const dates = weekDayDates(committed.weekStart)
        return {
            names: dates.map((day) => dateTimeFactory.formatCustom(day, "ccc")),
            dates: dates.map((day) => dateTimeFactory.formatCustom(day, "d MMM")),
            labels: dates.map((day) => dateTimeFactory.formatCustom(day, "cccc")),
        }
    }, [committed.weekStart, dateTimeFactory])

    const weekLabelFor = (start: DateTime) => {
        const end = start.plus({ days: 6 })
        const startLabel = dateTimeFactory.formatCustom(start, start.month === end.month ? "d" : "d MMM")
        return l10n.getString("merchant-week-menu-week-label", {
            week: start.weekNumber,
            period: `${startLabel}–${dateTimeFactory.formatCustom(end, "d MMM")}`,
        })
    }

    // Volatile data goes through stable getters, so typing in a cell does not invalidate the context.
    const weekRef = useRef(week)
    weekRef.current = week
    const allRowsRef = useRef(allRows)
    allRowsRef.current = allRows
    const getWeek = useCallback(() => weekRef.current, [])
    // Built when a title field asks for its suggestions, not on every keystroke of every cell.
    const getRecentDishes = useCallback(() => collectRecentDishes(weekRef.current, allRowsRef.current), [])

    // The counters are informational, so they follow a deferred copy of the week and stay off the
    // keystroke path: typing repaints the edited cell first and the badges catch up right after.
    const deferredWeek = useDeferredValue(week)
    const { readyCount, dayCount } = useMemo(
        () => countReadyDays(deferredWeek, allRows, days),
        [deferredWeek, allRows, days],
    )
    // A theme day replaces the template cells, so its header counts the theme's own dishes,
    // matching how countReadyDays decides day readiness.
    const dayFillProgress = useMemo(
        () =>
            days.map((day, dayIndex) =>
                day.theme ? countThemeFillProgress(day.theme) : countDayFillProgress(deferredWeek, allRows, dayIndex),
            ),
        [deferredWeek, allRows, days],
    )
    const rowFillProgress = useMemo(
        () => new Map(allRows.map((row) => [row.key, countRowFillProgress(deferredWeek, row)])),
        [deferredWeek, allRows],
    )
    const startedCellCount = (dayIndex: number) => startedCellCountForDay(week, allRows, dayIndex)

    const copyWeekTargets: ReadonlyArray<CopyWeekTarget> = Array.from(
        { length: COPY_WEEK_TARGET_COUNT },
        (_, offset) => {
            const start = committed.weekStart.plus({ weeks: offset + 1 })
            return {
                storeKey: `${activeTemplate?.id ?? "none"}:${weekKeyFor(start)}`,
                label: weekLabelFor(start),
            }
        },
    )

    const toggleCategory = (categoryIndex: number) =>
        setCollapsedCategories((collapsed) => {
            const next = new Set(collapsed)
            if (next.has(categoryIndex)) next.delete(categoryIndex)
            else next.add(categoryIndex)
            return next
        })

    const handleThemeSave = (name: string) => {
        const isNew = days[themeDialog.dayIndex].theme === null
        actions.setDayTheme(themeDialog.dayIndex, {
            name,
            dishes: days[themeDialog.dayIndex].theme?.dishes ?? [newThemeDish()],
        })
        if (isNew) {
            enqueueSnackbar({
                variant: "success",
                message: l10n.getString("merchant-week-menu-theme-created-snackbar", { name }),
            })
        }
        setThemeDialog((state) => ({ ...state, open: false }))
    }

    const showNotReadySnackbar = () =>
        enqueueSnackbar({ variant: "info", message: l10n.getString("merchant-week-menu-feature-not-ready-snackbar") })

    const showComingSoon = () => setComingSoonOpen(true)

    const runAiGeneration: typeof ai.generate = (settingsForDay, overwriteFilled, includedDayIndexes) => {
        setAiCustomizeOpen(false)
        setAiOverwriteOpen(false)
        ai.generate(settingsForDay, overwriteFilled, includedDayIndexes)
    }

    const handlePublish = () =>
        mockLoading.runWithLoading(() => {
            const publishableCount = days.filter(
                (day) => day.isPublishEnabled && !day.isClosed && day.status !== WeekDayPublishStatus.PUBLISHED,
            ).length
            actions.publishWeek()
            if (publishableCount > 0) {
                enqueueSnackbar({
                    variant: "success",
                    message: l10n.getString("merchant-week-menu-publish-count-snackbar", { count: publishableCount }),
                })
            } else {
                enqueueSnackbar({
                    variant: "info",
                    message: l10n.getString("merchant-week-menu-publish-none-snackbar"),
                })
            }
        })

    const handleClearWeek = () => {
        setClearWeekConfirmOpen(false)
        mockLoading.runWithLoading(() => {
            actions.clearWeek()
            enqueueSnackbar({ variant: "success", message: l10n.getString("merchant-week-menu-clear-week-snackbar") })
        })
    }

    const themeHasContent = (dayIndex: number) =>
        (days[dayIndex].theme?.dishes ?? []).some(
            ({ values }) =>
                values.title.trim() !== "" || values.description.trim() !== "" || values.price.trim() !== "",
        )

    const closeDay = (dayIndex: number) => {
        actions.setDayClosed(dayIndex, true)
        enqueueSnackbar({
            variant: "info",
            message: l10n.getString("merchant-week-menu-day-closed-snackbar", { day: dayStrings.labels[dayIndex] }),
        })
    }

    const removeTheme = (dayIndex: number) => {
        actions.setDayTheme(dayIndex, null)
        enqueueSnackbar({ variant: "info", message: l10n.getString("merchant-week-menu-theme-reverted-snackbar") })
    }

    // Closing drops the day's theme, so a theme with dishes has to be confirmed as well.
    const requestCloseDay = (dayIndex: number) => {
        if (startedCellCount(dayIndex) > 0 || themeHasContent(dayIndex)) {
            setDayConfirm({ action: DayConfirmAction.CLOSE_DAY, dayIndex })
        } else {
            closeDay(dayIndex)
        }
    }

    const requestRemoveTheme = (dayIndex: number) => {
        if (themeHasContent(dayIndex)) setDayConfirm({ action: DayConfirmAction.REMOVE_THEME, dayIndex })
        else removeTheme(dayIndex)
    }

    // Closing a day counts template cells; removing a theme counts the theme's own dishes.
    const confirmDishCount = ({ action, dayIndex }: DayConfirmState) =>
        action === DayConfirmAction.REMOVE_THEME
            ? (days[dayIndex].theme?.dishes.length ?? 0)
            : startedCellCount(dayIndex)

    const handleDayConfirm = () => {
        if (!dayConfirm) return
        if (dayConfirm.action === DayConfirmAction.CLOSE_DAY) closeDay(dayConfirm.dayIndex)
        else removeTheme(dayConfirm.dayIndex)
        setDayConfirm(null)
    }

    const onCellDragEnd = useCallback(() => setDragSource(null), [])

    const weekMenuContextValue = useMemo(
        () => ({
            actions,
            allergenOptions,
            getWeek,
            getRecentDishes,
            getAiDaySettings: ai.getAiDaySettings,
            dayLabels: dayStrings.labels,
            isAiReviewActive: ai.isBannerVisible,
            dragSource,
            onCellDragStart: setDragSource,
            onCellDragEnd,
        }),
        [
            actions,
            allergenOptions,
            getWeek,
            getRecentDishes,
            ai.getAiDaySettings,
            dayStrings,
            ai.isBannerVisible,
            dragSource,
            onCellDragEnd,
        ],
    )

    const isLoading = allergensQuery.isInitialLoading
    const isLoadFailed = !isLoading && activeTemplate !== undefined && templateDetails === undefined
    const hasNoTemplates = !isLoading && templates.length === 0

    if (isLoading) {
        return (
            <>
                {renderHeader()}
                <Stack spacing=".75rem">
                    <Skeleton variant="rounded" className={classes.templateTabsSkeleton} />
                    <Skeleton variant="rounded" className={classes.toolbarSkeleton} />
                    <WeekMenuTableSkeleton />
                </Stack>
            </>
        )
    }

    if (hasNoTemplates) {
        return (
            <>
                {renderHeader()}
                <WeekMenuEmptyState />
            </>
        )
    }

    if (isLoadFailed) {
        return (
            <>
                {renderHeader()}
                <StandardNoResultText noResultText={l10n.getString("merchant-week-menu-load-error")} />
            </>
        )
    }

    return (
        <WeekMenuProvider value={weekMenuContextValue}>
            {renderHeader(
                <WeekMenuHeaderActions
                    onQuickGenerate={showComingSoon}
                    onCustomize={showComingSoon}
                    onCustomerView={showComingSoon}
                    onPublish={handlePublish}
                />,
                <WeekMenuAutosavedLabel label={autosavedLabel} />,
            )}
            <Stack spacing=".75rem">
                <StandardSegmentedControl
                    options={templates.map((template) => ({ value: String(template.id), label: template.name }))}
                    value={selectedTemplate ? String(selectedTemplate.id) : undefined}
                    color="dark"
                    className={classes.templateTabs}
                    onChange={handleTemplateChange}
                />

                <WeekMenuToolbar
                    weekLabel={weekLabelFor(weekStart)}
                    readyDayCount={readyCount}
                    dayCount={dayCount}
                    onPreviousWeek={() => handleWeekChange(-1)}
                    onNextWeek={() => handleWeekChange(1)}
                    onManageTemplates={() => navigate(MERCHANT_ADMIN_SETTINGS_MENU_PLANNING_ROUTE)}
                    onCustomerPreview={showComingSoon}
                    onCopyWeek={() => setCopyWeekOpen(true)}
                    onClearWeek={() => setClearWeekConfirmOpen(true)}
                    onExportPdf={showNotReadySnackbar}
                    onPrintKitchenSheet={showNotReadySnackbar}
                />

                <Collapse in={ai.isBannerVisible} unmountOnExit className={classes.aiBannerAboveBackdrop}>
                    <AiBanner
                        isWorking={ai.isWorking}
                        templateName={activeTemplate?.name ?? ""}
                        onRegenerate={() => runAiGeneration(ai.getAiDaySettings, true)}
                        onUndo={ai.undo}
                        onClose={ai.closeBanner}
                    />
                </Collapse>

                {isSwitching ? (
                    <WeekMenuTableSkeleton />
                ) : (
                    <WeekMenuTable
                        // Remount per template and week, so cell-local UI state never leaks between them.
                        key={storeKey}
                        categories={categories}
                        week={week}
                        days={days}
                        isLoading={isPageBusy}
                        aiFilledKeys={aiFilledKeys}
                        dayNames={dayStrings.names}
                        dayDates={dayStrings.dates}
                        dayFillProgress={dayFillProgress}
                        rowFillProgress={rowFillProgress}
                        collapsedCategories={collapsedCategories}
                        onToggleCategory={toggleCategory}
                        onMakeTheme={(dayIndex) => setThemeDialog({ open: true, dayIndex, isNew: true })}
                        onRenameTheme={(dayIndex) => setThemeDialog({ open: true, dayIndex, isNew: false })}
                        onRemoveTheme={requestRemoveTheme}
                        onCloseDay={requestCloseDay}
                    />
                )}

                <WeekMenuLegend />
            </Stack>

            <Backdrop open={isPageBusy} invisible className={classes.workingBackdrop} />

            {themeDialog.open && (
                <ThemeNameDialog
                    open={themeDialog.open}
                    isNew={themeDialog.isNew}
                    dayName={dayStrings.labels[themeDialog.dayIndex]}
                    filledCount={startedCellCount(themeDialog.dayIndex)}
                    initialName={days[themeDialog.dayIndex].theme?.name ?? ""}
                    onSave={handleThemeSave}
                    onClose={() => setThemeDialog((state) => ({ ...state, open: false }))}
                />
            )}
            {isCopyWeekOpen && (
                <CopyWeekDialog
                    open={isCopyWeekOpen}
                    sourceTitle={activeTemplate?.name ?? ""}
                    sourcePeriod={weekLabelFor(committed.weekStart)}
                    targets={copyWeekTargets}
                    onClose={() => setCopyWeekOpen(false)}
                />
            )}
            {isAiOverwriteOpen && (
                <AiOverwriteDialog
                    open={isAiOverwriteOpen}
                    onChoose={(overwriteFilled) => runAiGeneration(() => defaultAiSettings(), overwriteFilled)}
                    onClose={() => setAiOverwriteOpen(false)}
                />
            )}
            {isAiCustomizeOpen && (
                <AiCustomizeDialog
                    open={isAiCustomizeOpen}
                    onGenerate={runAiGeneration}
                    onClose={() => setAiCustomizeOpen(false)}
                />
            )}
            {isClearWeekConfirmOpen && (
                <ClearWeekConfirmDialog
                    open={isClearWeekConfirmOpen}
                    onConfirm={handleClearWeek}
                    onClose={() => setClearWeekConfirmOpen(false)}
                />
            )}
            {dayConfirm && (
                <DayConfirmDialog
                    open
                    action={dayConfirm.action}
                    filledCount={confirmDishCount(dayConfirm)}
                    onConfirm={handleDayConfirm}
                    onClose={() => setDayConfirm(null)}
                />
            )}
            {isComingSoonOpen && <ComingSoonDialog open onClose={() => setComingSoonOpen(false)} />}
        </WeekMenuProvider>
    )
}
